import { Vector2 } from "../../../common/vector2.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { GLContext } from "../rendering/gl_context.js";
import { InstanceBuffer } from "../rendering/instance_buffer.js";
import { ShaderProgram } from "../rendering/shader_program.js";
import { ShaderCache } from "../rendering/shader_cache.js";
import { IBatchConfig, InstanceState } from "../types/core_types.js";
import { BlendMode, IAttributeLayout, ITextureBinding, AttributeSize, ICreateTextureOptions } from "../types/webgl_types.js";

export abstract class InstanceBatch<TLayout extends Record<string, AttributeSize> = any>
{
  protected shader!          : ShaderProgram;
  protected buffer!          : InstanceBuffer;
  protected active_count     : number  = 0;
  protected pending_count    : number  = 0;
  protected last_update_time : number  = 0;
  private is_initialized     : boolean = false;
  
  protected max_instances          : number = 0;
  protected blend_mode             : BlendMode = BlendMode.Normal;
  protected vertices_per_instance  : number = 0;
  protected primitive_type         : GLenum = 0;
  protected enable_z_sort          : boolean = false;
  protected attribute_layout       : IAttributeLayout[] = [];
  protected attr_offset!           : Record<keyof TLayout, number>;

  protected vertex_source          : string = '';
  protected fragment_source        : string = '';

  /** true якщо активні інстанси були додані ПІСЛЯ останнього uploadBuffer —
   *  тоді render() повинен перезавантажити буфер перед draw-call. */
  protected buffer_dirty  : boolean = false;
  protected needs_sort    : boolean = false;

  // Базові DOD-масиви для всіх ефектів
  protected start_times    !: Float32Array;
  protected durations      !: Float32Array;
  protected states         !: Uint8Array;
  protected stop_times     !: Float32Array;
  protected stop_durations !: Float32Array;
  protected group_ids      !: Int32Array;
  protected z_orders       !: Int32Array;
   // ID емітера-власника (0 = без групи)

  protected current_group_id: number = 0;  // встановлюється батчем перед emitInstance()
  
  protected textures    : ITextureBinding[] = [];

  private next_stable_id: number = 1;
  protected idx_to_stable !: Int32Array;
  protected stable_to_idx : Map<number, number> = new Map();

  protected onInit( gl: WebGL2RenderingContext ): void {}
  protected onBeforeRender( gl: WebGL2RenderingContext ): void {}
  protected onUpdate( now: number ): void {}
  
  // Тепер отримує готовий progress
  protected abstract updateInstance( gpu_data: Float32Array, offset: number, progress: number, now: number, idx: number ): boolean;
  protected onInstanceDeath( idx: number ): void {}
  protected onSwapInstance( idx_a: number, idx_b: number ): void {}
  public setInstancePosition( stable_id: number, pos: Vector2 ): void {}
  public setInstanceTransform( stable_id: number, pos: Vector2, angle: number ): void {}

  constructor( config: IBatchConfig<TLayout> )
  {
    this.max_instances         = config.max_instances;
    this.vertices_per_instance = config.vertices_per_instance;
    this.primitive_type        = config.primitive_type;
    
    if ( config.layout )
    {
      this.attribute_layout = [];
      this.attr_offset = {} as Record<keyof TLayout, number>;
      let current_offset = 0;
      let loc = 0;
      for ( const [name, size] of Object.entries( config.layout ) )
      {
        this.attribute_layout.push( { location: loc++, size: size as AttributeSize, name } );
        this.attr_offset[name as keyof TLayout] = current_offset;
        current_offset += ( size as number );
      }
    }
    else if ( config.attribute_layout )
    {
      this.attribute_layout = config.attribute_layout;
    }

    this.blend_mode            = config.blend_mode ?? BlendMode.Normal;
    this.enable_z_sort         = config.enable_z_sort ?? false;
  }

  protected buildShader( builder: ShaderBuilder ): void
  {
    const result = builder
      .withPrimitive( this.primitive_type )
      .withAttributes( this.attribute_layout )
      .build();
    
    this.vertex_source   = result.vertex_src;
    this.fragment_source = result.fragment_src;
  }

  public init( gl: WebGL2RenderingContext ): void
  {
    if ( this.is_initialized )
      return;
    
    this.is_initialized = true;

    this.start_times    = new Float32Array( this.max_instances );
    this.durations      = new Float32Array( this.max_instances );
    this.states         = new Uint8Array( this.max_instances );
    this.stop_times     = new Float32Array( this.max_instances );
    this.stop_durations = new Float32Array( this.max_instances );
    this.group_ids      = new Int32Array( this.max_instances );
    this.z_orders       = new Int32Array( this.max_instances );
    this.idx_to_stable  = new Int32Array( this.max_instances ).fill( -1 );
    this.stable_to_idx.clear();

    this.shader = ShaderCache.getProgram( gl, this.vertex_source, this.fragment_source );
    this.shader.bindUBO( gl, GLContext.GLOBAL_UBO_BLOCK_NAME, GLContext.GLOBAL_UBO_BINDING_POINT );
    
    this.buffer = new InstanceBuffer( gl, this.attribute_layout );
    this.buffer.ensureCapacity( this.max_instances ); 
    
    this.onInit( gl );
  }

  protected swapInstances( idx_a: number, idx_b: number ): void
  {
    if ( idx_a === idx_b )
      return;

    const data = this.buffer.getDataArray();
    const fpi  = this.buffer.floats_per_instance;
    const offset_a = idx_a * fpi;
    const offset_b = idx_b * fpi;

    for ( let f = 0; f < fpi; f++ )
    {
      const temp = data[offset_a + f];
      data[offset_a + f] = data[offset_b + f];
      data[offset_b + f] = temp;
    }

    const temp_st = this.start_times[idx_a];
    this.start_times[idx_a] = this.start_times[idx_b];
    this.start_times[idx_b] = temp_st;

    const temp_dur = this.durations[idx_a];
    this.durations[idx_a] = this.durations[idx_b];
    this.durations[idx_b] = temp_dur;

    const temp_state = this.states[idx_a];
    this.states[idx_a] = this.states[idx_b];
    this.states[idx_b] = temp_state;

    const temp_stop = this.stop_times[idx_a];
    this.stop_times[idx_a] = this.stop_times[idx_b];
    this.stop_times[idx_b] = temp_stop;

    const temp_stop_dur = this.stop_durations[idx_a];
    this.stop_durations[idx_a] = this.stop_durations[idx_b];
    this.stop_durations[idx_b] = temp_stop_dur;

    const temp_group = this.group_ids[idx_a];
    this.group_ids[idx_a] = this.group_ids[idx_b];
    this.group_ids[idx_b] = temp_group;

    const temp_z = this.z_orders[idx_a];
    this.z_orders[idx_a] = this.z_orders[idx_b];
    this.z_orders[idx_b] = temp_z;

    const stable_a = this.idx_to_stable[idx_a];
    const stable_b = this.idx_to_stable[idx_b];
    this.idx_to_stable[idx_a] = stable_b;
    this.idx_to_stable[idx_b] = stable_a;
    
    if ( stable_a !== -1 ) this.stable_to_idx.set( stable_a, idx_b );
    if ( stable_b !== -1 ) this.stable_to_idx.set( stable_b, idx_a );

    this.onSwapInstance( idx_a, idx_b );
  }

  /**
   * Виділяє пам'ять. Якщо duration <= 0, інстанс живе безкінечно (progress = 0).
   */
  protected allocateInstanceIndex( start_time: number, duration: number, z_order: number = 0 ): number
  {
    const total = this.active_count + this.pending_count;
    if ( total >= this.max_instances )
      return -1;

    const is_pending = start_time > this.last_update_time;
    let idx = -1;

    if ( is_pending )
    {
      idx = total;
      this.pending_count++;
    }
    else
    {
      idx = this.active_count;
      if ( this.pending_count > 0 )
        this.swapInstances( idx, total );
      this.active_count++;
      // Інстанс одразу активний — буфер потребує завантаження перед рендером.
      this.buffer_dirty = true;
      this.needs_sort = true;
    }

    this.start_times[idx] = start_time;
    this.durations[idx]   = duration;
    this.states[idx]      = InstanceState.Alive;
    this.stop_times[idx]  = 0;
    this.group_ids[idx]   = this.current_group_id;
    this.z_orders[idx]    = z_order;

    const stable_id = this.next_stable_id++;
    this.idx_to_stable[idx] = stable_id;
    this.stable_to_idx.set(stable_id, idx);

    return idx;
  }

  public getStableId(idx: number): number {
    return this.idx_to_stable[idx];
  }

  /**
   * Вбиває всі активні частинки конкретного емітера (group_id).
   * fade_duration = 0 → миттєво; > 0 → плавно.
   */
  public triggerDeathByGroup( group_id: number, current_time: number, fade_duration: number = 0 ): void
  {
    for ( let i = 0; i < this.active_count; i++ )
    {
      if ( this.group_ids[i] !== group_id ) continue;
      if ( this.states[i] !== InstanceState.Alive ) continue;

      this.states[i]         = InstanceState.Stopping;
      this.stop_times[i]     = current_time;
      this.stop_durations[i] = fade_duration;
    }
  }

  public triggerDeath( stable_id: number, current_time: number, fade_duration: number = 0 ): void 
  {
    const idx = this.stable_to_idx.get(stable_id);
    if ( idx === undefined || idx === -1 ) return;
    if ( this.states[idx] !== InstanceState.Alive ) return;

    this.states[idx] = InstanceState.Stopping;
    this.stop_times[idx] = current_time;
    this.stop_durations[idx] = fade_duration;
  }

  /** Пришвидшує анімацію інстансу в factor разів від моменту now. */
  public accelerateInstance( stable_id: number, factor: number, now: number ): void
  {
    const idx = this.stable_to_idx.get( stable_id );
    if ( idx === undefined || idx === -1 ) return;

    const elapsed = now - this.start_times[idx];
    const remaining = this.durations[idx] - elapsed;
    if ( remaining <= 0 ) return;

    this.start_times[idx] = now - elapsed * factor;
    this.durations[idx]   = ( elapsed + remaining ) / factor;
  }

  protected sortActiveInstancesIfNeeded(): void
  {
    // Сортуємо активні інстанси за z_order перед upload.
    // Використовуємо просте сортування вставками, бо масив зазвичай майже відсортований.
    if ( this.enable_z_sort && this.needs_sort && this.active_count > 1 )
    {
      for ( let i = 1; i < this.active_count; i++ )
      {
        const current_z = this.z_orders[i];
        let j = i - 1;
        while ( j >= 0 && this.z_orders[j] > current_z )
        {
          this.swapInstances( j, j + 1 );
          j--;
        }
      }
      this.needs_sort = false;
    }
    else if ( !this.enable_z_sort )
      this.needs_sort = false;
  }

  public updateBuffer( now: number, gl: WebGL2RenderingContext ): void
  {
    this.last_update_time = now;
    this.onUpdate( now );

    let p = 0;
    while ( p < this.pending_count )
    {
      const p_idx = this.active_count + p;
      const state = this.states[p_idx];
      let kill_pending = false;

      if ( state === InstanceState.Stopping )
      {
        const fade_duration = this.stop_durations[p_idx];
        const stop_time = this.stop_times[p_idx];
        if ( now > stop_time + fade_duration )
          kill_pending = true;
      }

      if ( kill_pending )
      {
        this.onInstanceDeath( p_idx );
        
        const stable_id = this.idx_to_stable[p_idx];
        if ( stable_id !== -1 )
        {
          this.stable_to_idx.delete( stable_id );
          this.idx_to_stable[p_idx] = -1;
        }

        const last_pending_idx = this.active_count + this.pending_count - 1;
        if ( p_idx !== last_pending_idx )
        {
          this.swapInstances( p_idx, last_pending_idx );
        }
        
        this.pending_count--;
      }
      else if ( now >= this.start_times[p_idx] )
      {
        if ( p_idx !== this.active_count )
          this.swapInstances( p_idx, this.active_count );
        
        this.active_count++;
        this.pending_count--;
        this.buffer_dirty = true;
        this.needs_sort = true;
      }
      else
      {
        p++;
      }
    }

    this.sortActiveInstancesIfNeeded();

    if ( this.active_count === 0 )
      return;

    const data = this.buffer.getDataArray();
    const fpi  = this.buffer.floats_per_instance;

    let i = 0;
    while ( i < this.active_count )
    {
      const offset = i * fpi;
      
      let is_alive = true;
      let progress = 0.0;

      const state = this.states[i];
      if ( state === InstanceState.Stopping ) 
      {
        const fade_duration = this.stop_durations[i];
        const stop_time = this.stop_times[i];
        if ( now > stop_time + fade_duration )
          is_alive = false;
        else
        {
          const fade_p = fade_duration > 0 ? Math.max( 0, Math.min( 1, ( now - stop_time ) / fade_duration ) ) : 1.0;
          const dur = this.durations[i];
          if ( dur > 0 )
          {
            const age = now - this.start_times[i];
            const natural_p = Math.min( 1.0, age / dur );
            progress = Math.max( natural_p, fade_p );
          }
          else
            progress = fade_p;
        }
      }
      else
      {
        const age = now - this.start_times[i];
        const dur = this.durations[i];

        if ( dur > 0 )
        {
          if ( age >= dur ) 
            is_alive = false;
          else 
            progress = age / dur;
        }
      }

      if ( is_alive )
      {
        // Виклик логіки нащадка
        is_alive = this.updateInstance( data, offset, progress, now, i );
      }

      if ( !is_alive )
      {
        this.onInstanceDeath( i );
        
        const stable_id = this.idx_to_stable[i];
        if ( stable_id !== -1 )
        {
          this.stable_to_idx.delete( stable_id );
          this.idx_to_stable[i] = -1;
        }
        
        const last_active_idx = this.active_count - 1;
        
        if ( i !== last_active_idx )
        {
          this.swapInstances( i, last_active_idx );
          this.needs_sort = true;
        }
        
        this.active_count--;

        if ( this.pending_count > 0 )
        {
          const hole_idx = this.active_count;
          const last_pending_idx = this.active_count + this.pending_count; // Corrected: active_count was already decremented
          
          this.swapInstances( hole_idx, last_pending_idx );
        }
      }
      else
        i++;
    }

    if ( this.active_count > 0 )
    {
      this.sortActiveInstancesIfNeeded();
      this.buffer.upload( gl, this.active_count );
      this.buffer_dirty = false;
    }
  }

  public render( gl: WebGL2RenderingContext ): void
  {
    if ( this.active_count === 0 )
      return;

    // Якщо інстанси були заспавнені після updateBuffer (buffer_dirty),
    // перезавантажуємо GPU-буфер перед draw-call, щоб уникнути порожнього кадру.
    if ( this.buffer_dirty )
    {
      // Якщо Z-порядок порушено (новий інстанс доданий після updateBuffer),
      // сортуємо прямо тут, перед завантаженням у GPU.
      this.sortActiveInstancesIfNeeded();

      this.buffer.upload( gl, this.active_count );
      this.buffer_dirty = false;
    }

    this.shader.use( gl );
    this.buffer.bind( gl );

    for ( let i = 0; i < this.textures.length; i++ )
    {
      const t = this.textures[i];
      gl.activeTexture( gl.TEXTURE0 + t.unit );
      gl.bindTexture( t.target || gl.TEXTURE_2D, t.texture );
      gl.uniform1i( this.shader.getLocation( gl, t.location ), t.unit );
    }

    this.onBeforeRender( gl );

    this.applyBlendState( gl );

    gl.drawArraysInstanced( this.primitive_type, 0, this.vertices_per_instance, this.active_count );
    this.buffer.unbind( gl );
  }

  /**
   * Справжній прогрів для мобільних браузерів (Telegram WebView / iOS Safari).
   * Використовує малювання 1 інстансу з увімкненим colorMask (щоб згенерувати правильний PSO).
   * Оскільки буфер заповнений нулями, розмір і альфа об'єкта будуть 0 (невидимий).
   */
  public warmup( gl: WebGL2RenderingContext ): void
  {
    if ( !this.is_initialized ) return;

    this.shader.use( gl );
    this.buffer.bind( gl );

    for ( let i = 0; i < this.textures.length; i++ )
    {
      const t = this.textures[i];
      gl.activeTexture( gl.TEXTURE0 + t.unit );
      gl.bindTexture( t.target || gl.TEXTURE_2D, t.texture );
      gl.uniform1i( this.shader.getLocation( gl, t.location ), t.unit );
    }

    this.onBeforeRender( gl );
    this.applyBlendState( gl );
    
    // ВАЖЛИВО: Використовуємо SCISSOR_TEST (1x1 піксель у кутку), щоб виключити візуальні артефакти,
    // але гарантувати виконання растерізатора та Fragment Shader мобільними GPU.
    gl.enable( gl.SCISSOR_TEST );
    gl.scissor( 0, 0, 1, 1 );

    const gpu_data = this.buffer.getDataArray();
    this.fillWarmupInstance( gpu_data, 0 );
    
    this.buffer.upload( gl, 1 );

    // Малюємо 1 інстанс із НОРМАЛЬНИМ colorMask (щоб драйвер згенерував повний PSO)
    gl.drawArraysInstanced( this.primitive_type, 0, this.vertices_per_instance, 1 );

    gl.disable( gl.SCISSOR_TEST );
    this.buffer.unbind( gl );
  }

  /**
   * Заповнює атрибути першого інстансу для прогріву шейдера.
   * Підкласи можуть перевизначати цей метод під власні атрибути.
   */
  protected fillWarmupInstance( gpu_data: Float32Array, offset: number ): void
  {
    if ( !this.attr_offset ) return;

    if ( 'a_position' in this.attr_offset )
    {
      gpu_data[offset + (this.attr_offset as any).a_position] = 100;
      gpu_data[offset + (this.attr_offset as any).a_position + 1] = 100;
    }
    if ( 'a_size' in this.attr_offset )
    {
      gpu_data[offset + (this.attr_offset as any).a_size] = 50;
      gpu_data[offset + (this.attr_offset as any).a_size + 1] = 50;
    }
    if ( 'a_opacity' in this.attr_offset )
    {
      gpu_data[offset + (this.attr_offset as any).a_opacity] = 0;
    }
    if ( 'a_timing' in this.attr_offset )
    {
      gpu_data[offset + (this.attr_offset as any).a_timing] = performance.now() + 100000;
    }
  }

  public killAll(): void
  {
    this.onKillAll();
    
    for ( let i = 0; i < this.active_count + this.pending_count; i++ )
    {
      this.onInstanceDeath( i );
      
      const stable_id = this.idx_to_stable[i];
      if ( stable_id !== -1 )
        this.stable_to_idx.delete( stable_id );
      
      this.idx_to_stable[i] = -1;
    }
    
    this.active_count = 0;
    this.pending_count = 0;
  }

  public hasInstances(): boolean
  {
    return this.active_count > 0 || this.pending_count > 0;
  }

  public getActiveCount(): number { return this.active_count; }
  
  public getPendingCount(): number { return this.pending_count; }

  protected onKillAll(): void {}

  public dispose( gl: WebGL2RenderingContext ): void
  {
    this.killAll();
    if ( this.shader )
      ShaderCache.releaseProgram( gl, this.shader );
    if ( this.buffer )
      this.buffer.dispose( gl );
    this.textures.length = 0;
    this.stable_to_idx.clear();
  }

  protected createTexture( image: TexImageSource, gl: WebGL2RenderingContext, options: ICreateTextureOptions = {} ): WebGLTexture
  {
    const {
      wrap_s           = gl.CLAMP_TO_EDGE,
      wrap_t           = gl.CLAMP_TO_EDGE,
      min_filter       = gl.LINEAR_MIPMAP_LINEAR,
      mag_filter       = gl.LINEAR,
      generate_mipmaps = true,
      flip_y           = true,
    } = options;

    const tex = gl.createTexture();
    if ( !tex )
      throw new Error( "Texture creation failed" );

    gl.bindTexture( gl.TEXTURE_2D, tex );
    gl.pixelStorei( gl.UNPACK_FLIP_Y_WEBGL, flip_y );
    gl.texImage2D( gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap_s );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap_t );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, mag_filter );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, min_filter );

    if ( generate_mipmaps )
      gl.generateMipmap( gl.TEXTURE_2D );
    return tex;
  }

  private applyBlendState( gl: WebGL2RenderingContext ): void
  {
    if ( this.blend_mode === BlendMode.None )
    {
      gl.disable( gl.BLEND );
      return;
    }

    gl.enable( gl.BLEND );
    // Reset to FUNC_ADD by default, Max will override it
    gl.blendEquation( gl.FUNC_ADD );

    switch ( this.blend_mode )
    {
      case BlendMode.Normal:
        gl.blendFunc( gl.ONE, gl.ONE_MINUS_SRC_ALPHA );
        break;
      case BlendMode.Additive:
        gl.blendFunc( gl.ONE, gl.ONE );
        break;
      case BlendMode.Max:
        gl.blendEquationSeparate( gl.MAX, gl.MAX );
        gl.blendFuncSeparate( gl.ONE, gl.ONE, gl.ONE, gl.ONE );
        break;
    }
  }
}